import { useCallback, useEffect, useState } from "react";
//old import { FileText, Info, ListChecks, Megaphone, MessageSquare, Mic, Send } from "lucide-react";
//old import { FileText, Info, ListChecks, Megaphone, Mic, Send } from "lucide-react";
import { FileText, Info, ListChecks, Megaphone, Mic, Send, Upload } from "lucide-react";
import api from "../../api/api";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
//old import { Alert, EmptyState, Field, OrgModal, Panel, Pill, apiError, formatDate, useOrgStyles } from "../../components/org/orgUi";
import { Alert, Field, OrgModal, Panel, Pill, apiError, useOrgStyles } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

/* Checklists & Scoring (Evan, Oct 9): how the AI features check and score this
   organization's students, and the org's change requests to Pono Pilot
   with their status. It shows how scoring behaves, never the actual prompt: the
   backend sends only category names, weights and descriptions, and How it works
   is written here. Org admins only (adminOnly in ORG_PAGES). */
/* Oct 16 (Evan): the org's requests and the Request a change form moved out to
   their own Requests page (OrgRequests.js); this page only links there.
   RequestModal stays here next to partOptions and is exported for that page. */

/** What a change request is about (OrganizationChangeRequests.FeatureName). Also used by Admin → Organizations. */
export const FEATURE_LABEL = {
    org_checklist: "Resume Checklist",
    resume_analysis: "Resume Analysis",
    mock_interview: "Mock Interview",
    elevator_pitch: "Elevator Pitch",
    other: "Something else",
    // asked for on the Licenses page with Buy more licenses (JJ, Nov 2), not with Request a change
    more_licenses: "More licenses",
};

/** A change request's status as the organization sees it. Also used by Admin → Organizations. */
export const CHANGE_STATUS = {
    pending: { label: "Sent · waiting for review", tone: "warning" },
    in_progress: { label: "In progress", tone: "primary" },
    done: { label: "Done", tone: "success" },
    rejected: { label: "Declined", tone: "danger" },
};

// Each feature in plain words, written from its scoring prompt in ai_service.py
// (FALLBACK_ORG_CHECKLIST_PROMPT, FALLBACK_RESUME_PROMPT,
// FALLBACK_INTERVIEW_ANALYSIS_PROMPT + FALLBACK_STAR_ANALYSIS_PROMPT,
// FALLBACK_PITCH_ANALYSIS_PROMPT). Kept here instead of coming from the backend,
// so this page can never carry a prompt. Update it when those prompts change.
const HOW_IT_WORKS = {
    //old org_checklist: "Students run Resume Checklist on an uploaded resume. Each rule below gets Pass or Fail with a one-line reason that points to the resume. Page count, photos, text color, fonts and bullet style are measured from the file itself. Only these rules are checked, not skills or job fit. Students who don't enter an organization code are checked against this list.",
    // (which students get the list is said under its name: the org's own needs its code)
    //old org_checklist: "Students run Resume Checklist on an uploaded resume. Each rule below gets Pass or Fail with a one-line reason that points to the resume. Page count, photos, text color, fonts and bullet style are measured from the file itself. Only these rules are checked, not skills or job fit.",
    org_checklist: "Clients run Resume Checklist on an uploaded resume. Each rule below gets Pass or Fail with a one-line reason that points to the resume. Page count, photos, text color, fonts and bullet style are measured from the file itself. Only these rules are checked, not skills or job fit.",
    //old resume_analysis: "Students score a resume against a job description they choose. Each category below gets a 0–100 score with feedback that points to specific lines on the resume; the overall score is the weighted average using the weights shown.",
    resume_analysis: "Clients score a resume against a job description they choose. Each category below gets a 0–100 score with feedback that points to specific lines on the resume; the overall score is the weighted average using the weights shown.",
    //old mock_interview: "Each practice interview has 4 questions (2 behavioral, 1 role-specific, 1 leadership). Every answer gets strengths, things to improve and delivery notes, and the session gets a 0–100 score on each category below: delivery categories are judged on how the student spoke, content categories on what they said. Behavioral answers also get a STAR breakdown.",
    mock_interview: "Each practice interview has 4 questions (2 behavioral, 1 role-specific, 1 leadership). Every answer gets strengths, things to improve and delivery notes, and the session gets a 0–100 score on each category below: delivery categories are judged on how the client spoke, content categories on what they said. Behavioral answers also get a STAR breakdown.",
    //old elevator_pitch: "Students record one 30, 60 or 90-second pitch for a networking, recruiter or investor setting, optionally for a target role. It gets a 0–100 score on each category below, judged for that setting and for how well the time was used, plus strengths, things to improve and delivery notes.",
    elevator_pitch: "Clients record one 30, 60 or 90-second pitch for a networking, recruiter or investor setting, optionally for a target role. It gets a 0–100 score on each category below, judged for that setting and for how well the time was used, plus strengths, things to improve and delivery notes.",
};

const SCOPE_LABEL = { organization: "Custom for your organization", default: "Pono Pilot default" };

const shorten = (text) => (text.length > 90 ? `${text.slice(0, 90)}…` : text);

/** "Which part?" choices: the rules or categories shown on this page. A rule's
 *  value is its own text (no "Rule N:"), so it always fits the request. */
function partOptions(featureName, checklist, scoring) {
    const names = (categories) => (categories || []).map((c) => ({ value: c.Name, label: c.Name }));
    if (featureName === "org_checklist") {
        return (checklist?.rules || []).map((rule, i) => ({ value: rule, label: `Rule ${i + 1}: ${shorten(rule)}` }));
    }
    if (featureName === "resume_analysis") return names(scoring?.resume?.categories);
    if (featureName === "mock_interview") {
        return [
            ...names(scoring?.interview?.categories),
            ...(scoring?.interview?.star || []).map((c) => ({ value: `STAR: ${c.Name}`, label: `STAR: ${c.Name}` })),
        ];
    }
    if (featureName === "elevator_pitch") return names(scoring?.pitch?.categories);
    return [];
}

function PanelTitle({ icon: Icon, children }) {
    return <span className="inline-flex items-center gap-2"><Icon size={18} /> {children}</span>;
}

function HowItWorks({ text }) {
    const s = useOrgStyles();
    return (
        <div className={`mb-4 rounded-xl border p-3 text-sm ${s.subPanel}`}>
            <div className={`mb-1 text-xs font-semibold uppercase tracking-wide ${s.muted}`}>How it works</div>
            <p className={s.body}>{text}</p>
        </div>
    );
}

/** Category names with weight bars, like the criteria on a program's page (OrgPrograms CriteriaPanel). */
function CategoryList({ categories }) {
    const s = useOrgStyles();
    return (
        <ul className="space-y-2">
            {categories.map((c, i) => (
                <li key={`${c.Name}-${i}`}>
                    <div className="flex items-center justify-between gap-3 text-sm">
                        <span className={`min-w-0 break-words font-medium ${s.text}`}>{c.Name}</span>
                        <span className={`tabular-nums ${s.muted}`}>{c.Weight}%</span>
                    </div>
                    <div className={`mt-1 h-1.5 overflow-hidden rounded-full ${s.dark ? "bg-slate-700" : "bg-slate-100"}`}>
                        <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, c.Weight)}%` }} />
                    </div>
                    {c.Description && <p className={`mt-1 break-words text-xs ${s.muted}`}>{c.Description}</p>}
                </li>
            ))}
        </ul>
    );
}

/** A program can have its own resume or interview criteria; its page shows them. */
function ProgramsNote({ onOpen }) {
    const s = useOrgStyles();
    return (
        <p className={`mt-4 text-sm ${s.muted}`}>
            A program can have its own criteria. Open it under{" "}
            <button type="button" className={s.btnLink} onClick={onOpen}>Programs</button>
            {/* old {" "}to see exactly what its students get. */}
            {" "}to see exactly what its clients get.
        </p>
    );
}

function ScoringPanel({ icon, title, how, pickLabel, criteria, error, actions, children }) {
    const s = useOrgStyles();
    return (
        <Panel
            title={<PanelTitle icon={icon}>{title}</PanelTitle>}
            actions={<>
                {criteria && <Pill tone={criteria.Scope === "default" ? "neutral" : "primary"}>{SCOPE_LABEL[criteria.Scope]}</Pill>}
                {actions}
            </>}
        >
            <HowItWorks text={how} />
            {!criteria ? (
                <>
                    <Alert>{error}</Alert>
                    {!error && <InlineTableSkeleton isDarkMode={s.dark} rows={4} cols={2} bordered={false} />}
                </>
            ) : (
                <>
                    {criteria.Scope === "organization" && (
                        <div className="mb-4">
                            <p className={`break-words text-sm font-semibold ${s.text}`}>{criteria.Name}</p>
                            {criteria.Description && <p className={`mt-1 break-words text-sm ${s.body}`}>{criteria.Description}</p>}
                            <p className={`mt-1 text-xs ${s.muted}`}>
                                {/* old Students get these when they pick one of your programs under {pickLabel}; otherwise the Pono Pilot default applies. */}
                                Clients get these when they pick one of your programs under {pickLabel}; otherwise the Pono Pilot default applies.
                            </p>
                        </div>
                    )}
                    <CategoryList categories={criteria.categories} />
                    {children}
                </>
            )}
        </Panel>
    );
}

export default function OrgScoring() {
    const { orgId, goTo } = useOrgPortal();
    const s = useOrgStyles();
    // Each part loads on its own (null while loading), so one failing doesn't hide the others.
    //old const [checklist, setChecklist] = useState(null);
    //old const [checklistError, setChecklistError] = useState("");
    const [scoring, setScoring] = useState(null);
    const [scoringError, setScoringError] = useState("");
    // The Resume Checklist the org's students get comes with the scoring (its own with its code, else
    // the standard one), no longer always the standard list from /resume/checklist-rules
    const checklist = scoring?.checklist;
    //old const [requests, setRequests] = useState(null);
    //old const [requestsError, setRequestsError] = useState("");
    //old const [notice, setNotice] = useState("");
    //old const [asking, setAsking] = useState(null); // null = closed; otherwise the feature the request starts with ("" = none)

    //old // The standard list (no code): what students who don't enter an organization code are checked against
    //old const loadChecklist = useCallback(async () => {
        //old try {
            //old const res = await api.get("/resume/checklist-rules");
            //old setChecklist({ name: res.data.name, rules: res.data.rules || [] });
        //old } catch (err) {
            //old setChecklistError(apiError(err, "Couldn't load the checklist."));
        //old }
    //old }, []);

    const loadScoring = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/scoring`);
            setScoring(res.data);
        } catch (err) {
            setScoringError(apiError(err, "Couldn't load how scoring works."));
        }
    }, [orgId]);

    //old const loadRequests = useCallback(async () => {
        //old try {
            //old const res = await api.get(`/org/${orgId}/change-requests`);
            //old setRequests(res.data.requests || []);
            //old setRequestsError("");
        //old } catch (err) {
            //old setRequestsError(apiError(err, "Couldn't load your requests."));
        //old }
    //old }, [orgId]);

    //old useEffect(() => { loadChecklist(); loadScoring(); loadRequests(); }, [loadChecklist, loadScoring, loadRequests]);
    //old useEffect(() => { loadChecklist(); loadScoring(); }, [loadChecklist, loadScoring]);
    useEffect(() => { loadScoring(); }, [loadScoring]);

    //old const ask = (featureName) => {
        //old setNotice("");
        //old setAsking(featureName);
    //old };
    // Requests moved out to their own page: no Request a change buttons here any more
    //old const requestButton = (featureName) => (
        //old <button type="button" className={s.btnSm} onClick={() => ask(featureName)}>
            //old <MessageSquare size={12} /> Request a change
        //old </button>
    //old );
    //old const onSent = async () => {
        //old setAsking(null);
        //old setNotice("Request sent. Pono Pilot will update its status here.");
        //old await loadRequests();
    //old };

    return (
        <div className="space-y-6">
            {/* old <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert> */}
            <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${s.subPanel}`}>
                <Info size={18} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                <p className={s.body}>
                    {/* old These are the rules and scoring your students get today. To change something, send a request; */}
                    {/* old Pono Pilot reviews it and updates its status below. */}
                    {/* old These are the rules and scoring your students get today. To change something, send a request on the{" "} */}
                    These are the rules and scoring your clients get today. To change something, send a request on the{" "}
                    <button type="button" className={s.btnLink} onClick={() => goTo("requests")}>Requests</button> page;
                    Pono Pilot reviews it and you can follow its status there.
                </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {/* old <Panel title={<PanelTitle icon={ListChecks}>Resume Checklist</PanelTitle>} actions={requestButton("org_checklist")}> */}
                {/* old <Panel title={<PanelTitle icon={ListChecks}>Resume Checklist</PanelTitle>}> */}
                {/* the org's own list or the standard one, marked like the scoring panels */}
                <Panel title={<PanelTitle icon={ListChecks}>Resume Checklist</PanelTitle>}
                       actions={checklist && <Pill tone={checklist.Scope === "default" ? "neutral" : "primary"}>{SCOPE_LABEL[checklist.Scope]}</Pill>}>
                    <HowItWorks text={HOW_IT_WORKS.org_checklist} />
                    {!checklist ? (
                        <>
                            {/* old <Alert>{checklistError}</Alert> */}
                            {/* old {!checklistError && <InlineTableSkeleton isDarkMode={s.dark} rows={5} cols={1} bordered={false} />} */}
                            <Alert>{scoringError}</Alert>
                            {!scoringError && <InlineTableSkeleton isDarkMode={s.dark} rows={5} cols={1} bordered={false} />}
                        </>
                    ) : (
                        <>
                            {/* old <p className={`mb-2 text-sm font-semibold ${s.text}`}>{checklist.name}</p> */}
                            <p className={`break-words text-sm font-semibold ${s.text}`}>{checklist.Name}</p>
                            {/* who gets it: the org's own needs the code its students enter */}
                            <p className={`mb-3 mt-1 text-xs ${s.muted}`}>
                                {checklist.Code
                                    //old ? <>Students get these when they enter <strong className={s.text}>{checklist.Code}</strong> as their Organization Code on Resume Checklist; otherwise the Pono Pilot standard checklist applies.</>
                                    ? <>Clients get these when they enter <strong className={s.text}>{checklist.Code}</strong> as their Organization Code on Resume Checklist; otherwise the Pono Pilot standard checklist applies.</>
                                    //old : "Students who don't enter an organization code are checked against this list."}
                                    : "Clients who don't enter an organization code are checked against this list."}
                            </p>
                            <ol className={`list-decimal space-y-1.5 pl-5 text-sm ${s.body}`}>
                                {checklist.rules.map((rule, i) => <li key={i} className="break-words">{rule}</li>)}
                            </ol>
                        </>
                    )}
                </Panel>
                {/* old criteria={scoring?.resume} error={scoringError} actions={requestButton("resume_analysis")}> */}
                <ScoringPanel icon={FileText} title="Resume Analysis" how={HOW_IT_WORKS.resume_analysis} pickLabel="Evaluate Against"
                              criteria={scoring?.resume} error={scoringError}>
                    <ProgramsNote onOpen={() => goTo("programs")} />
                </ScoringPanel>
                {/* old criteria={scoring?.interview} error={scoringError} actions={requestButton("mock_interview")}> */}
                <ScoringPanel icon={Mic} title="Mock Interview" how={HOW_IT_WORKS.mock_interview} pickLabel="Scoring criteria"
                              criteria={scoring?.interview} error={scoringError}>
                    <div className={`mt-5 border-t pt-4 ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                        <p className={`mb-3 text-sm font-semibold ${s.text}`}>STAR breakdown</p>
                        <CategoryList categories={scoring?.interview?.star || []} />
                    </div>
                    <ProgramsNote onOpen={() => goTo("programs")} />
                </ScoringPanel>
                {/* old criteria={scoring?.pitch} error={scoringError} actions={requestButton("elevator_pitch")} /> */}
                <ScoringPanel icon={Megaphone} title="Elevator Pitch" how={HOW_IT_WORKS.elevator_pitch}
                              criteria={scoring?.pitch} error={scoringError} />
            </div>

            {/* old <Panel title={<PanelTitle icon={MessageSquare}>Your requests</PanelTitle>} actions={requestButton("")} padded={false}> */}
                {/* old {requestsError && ( */}
                    {/* old <div className="px-5 pt-4"> */}
                        {/* old <Alert onClose={requests ? () => setRequestsError("") : undefined}>{requestsError}</Alert> */}
                    {/* old </div> */}
                {/* old )} */}
                {/* old {requests === null ? ( */}
                    {/* old !requestsError && <InlineTableSkeleton isDarkMode={s.dark} rows={3} cols={4} bordered={false} /> */}
                {/* old ) : requests.length === 0 ? ( */}
                    {/* old <EmptyState icon={MessageSquare} title="No requests yet" */}
                                {/* old body="Use Request a change on any part above to ask Pono Pilot to change it." /> */}
                {/* old ) : ( */}
                    {/* old <div className="relative overflow-x-auto"> */}
                        {/* old <table className="w-full"> */}
                            {/* old <thead> */}
                                {/* old <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}> */}
                                    {/* old <th className={s.th}>Sent</th> */}
                                    {/* old <th className={s.th}>About</th> */}
                                    {/* old <th className={s.th}>Request</th> */}
                                    {/* old <th className={s.th}>Status</th> */}
                                {/* old </tr> */}
                            {/* old </thead> */}
                            {/* old <tbody> */}
                                {/* old {requests.map((r) => ( */}
                                    {/* old <tr key={r.ChangeRequestID} className={`border-b last:border-0 ${s.row}`}> */}
                                        {/* old <td className={`${s.td} whitespace-nowrap`}>{formatDate(r.CreatedOn)}</td> */}
                                        {/* old <td className={s.td}> */}
                                            {/* old <div className={`font-semibold ${s.text}`}>{FEATURE_LABEL[r.FeatureName] || r.FeatureName}</div> */}
                                            {/* old {r.TargetItem && <div className={`mt-1 max-w-xs break-words text-xs ${s.muted}`}>{r.TargetItem}</div>} */}
                                        {/* old </td> */}
                                        {/* old <td className={s.td}> */}
                                            {/* old <div className="min-w-[14rem] max-w-md whitespace-pre-wrap break-words">{r.Details}</div> */}
                                        {/* old </td> */}
                                        {/* old <td className={s.td}> */}
                                            {/* old <Pill tone={(CHANGE_STATUS[r.Status] || {}).tone}>{(CHANGE_STATUS[r.Status] || {}).label || r.Status}</Pill> */}
                                            {/* old {r.ReviewNote && <div className={`mt-1 max-w-xs whitespace-pre-wrap break-words text-xs ${s.body}`}>{r.ReviewNote}</div>} */}
                                            {/* old {r.ReviewedOn && <div className={`mt-1 text-xs ${s.muted}`}>Updated {formatDate(r.ReviewedOn)}</div>} */}
                                        {/* old </td> */}
                                    {/* old </tr> */}
                                {/* old ))} */}
                            {/* old </tbody> */}
                        {/* old </table> */}
                    {/* old </div> */}
                {/* old )} */}
            {/* old </Panel> */}

            {/* old {asking !== null && ( */}
                {/* old <RequestModal orgId={orgId} featureName={asking} checklist={checklist} scoring={scoring} */}
                              {/* old onClose={() => setAsking(null)} onSent={onSent} /> */}
            {/* old )} */}
        </div>
    );
}

// A request can come with a PDF, e.g. the organization's own checklist (Evan, Sep 30: "as a
// scanned-and-validated PDF or as text"); the backend checks the same: a PDF, not empty, up to 20 MB
const ATTACH_MAX_MB = 20;
const attachProblem = (file) => (!file.name.toLowerCase().endsWith(".pdf") ? "Attach a PDF."
    : file.size === 0 ? "That file is empty."
        : file.size > ATTACH_MAX_MB * 1024 * 1024 ? `That file is over ${ATTACH_MAX_MB} MB.` : "");

/** Ask Pono Pilot to change a checklist or scoring. */
//old function RequestModal({ orgId, featureName, checklist, scoring, onClose, onSent }) {
export function RequestModal({ orgId, featureName, checklist, scoring, onClose, onSent }) {
    const s = useOrgStyles();
    const [form, setForm] = useState({ featureName, targetItem: "", details: "" });
    const [file, setFile] = useState(null); // the optional PDF
    const [fileError, setFileError] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const parts = partOptions(form.featureName, checklist, scoring);
    const addNew = form.featureName === "org_checklist" ? "Add a new rule" : "Add a new category";

    // checked as it's chosen, so a wrong file says so before anything is sent
    const pickFile = (e) => {
        const picked = e.target.files?.[0] || null;
        e.target.value = ""; // picking the same file again still picks it
        if (!picked) return;
        const problem = attachProblem(picked);
        setFileError(problem);
        setFile(problem ? null : picked);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            //old await api.post(`/org/${orgId}/change-requests`, {
            const fields = {
                featureName: form.featureName,
                targetItem: form.targetItem || undefined,
                details: form.details.trim(),
            //old });
            };
            if (file) {
                // with a PDF it goes as multipart: the fields as JSON in "data", the file in "attachment"
                // (the shared JSON Content-Type is unset so the browser sends multipart, as Settings does)
                const data = new FormData();
                data.append("data", JSON.stringify(fields));
                data.append("attachment", file);
                await api.post(`/org/${orgId}/change-requests`, data, { headers: { "Content-Type": undefined } });
            } else {
                await api.post(`/org/${orgId}/change-requests`, fields);
            }
            await onSent();
        } catch (err) {
            setError(apiError(err));
            setSaving(false);
        }
    };

    return (
        <OrgModal
            isOpen
            onClose={onClose}
            title="Request a change"
            footer={<>
                <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" form="change-request" className={s.btnPrimary}
                        disabled={saving || !form.featureName || !form.details.trim()}>
                    <Send size={14} /> Send request
                </button>
            </>}
        >
            <Alert onClose={() => setError("")}>{error}</Alert>
            <form id="change-request" onSubmit={submit} className="space-y-4">
                <Field label="What is it about?" htmlFor="cr-feature">
                    {/* picking another feature clears Which part? */}
                    <select id="cr-feature" className={s.input} value={form.featureName} required
                            onChange={(e) => setForm((f) => ({ ...f, featureName: e.target.value, targetItem: "" }))}>
                        <option value="">Choose…</option>
                        {/* old {Object.entries(FEATURE_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)} */}
                        {/* (not More licenses: that's asked for on the Licenses page, with Buy more licenses) */}
                        {Object.entries(FEATURE_LABEL).filter(([key]) => key !== "more_licenses")
                            .map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                </Field>
                {form.featureName && form.featureName !== "other" && (
                    <Field label="Which part? (optional)" htmlFor="cr-part">
                        <select id="cr-part" className={s.input} value={form.targetItem} onChange={set("targetItem")}>
                            <option value="">Not sure</option>
                            {parts.map((p, i) => <option key={i} value={p.value}>{p.label}</option>)}
                            <option value={addNew}>{addNew}</option>
                        </select>
                    </Field>
                )}
                <Field label="What should change?" htmlFor="cr-details">
                    <textarea id="cr-details" rows={5} maxLength={2000} required className={s.input}
                              //old placeholder="e.g. Students should say their name at the start of the elevator pitch"
                              placeholder="e.g. Clients should say their name at the start of the elevator pitch"
                              value={form.details} onChange={set("details")} />
                </Field>
                <Field label="Attach a PDF (optional)" htmlFor="cr-file" hint={`For example your own checklist. Up to ${ATTACH_MAX_MB} MB.`}>
                    {file ? (
                        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${s.subPanel}`}>
                            <FileText size={16} className={`shrink-0 ${s.muted}`} aria-hidden="true" />
                            <span className={`min-w-0 flex-1 [overflow-wrap:anywhere] ${s.text}`}>{file.name}</span>
                            <button type="button" className={s.btnLink} onClick={() => setFile(null)}>Remove</button>
                        </div>
                    ) : (
                        <label className={`inline-flex cursor-pointer items-center gap-2 focus-within:ring-2 focus-within:ring-primary-400 ${s.btnSecondary}`}>
                            <Upload size={14} /> Choose file
                            <input id="cr-file" type="file" accept=".pdf,application/pdf" className="sr-only" onChange={pickFile} />
                        </label>
                    )}
                    {fileError && <p className={`mt-1 text-xs ${s.dark ? "text-red-300" : "text-red-600"}`}>{fileError}</p>}
                </Field>
            </form>
        </OrgModal>
    );
}
